import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Select, Alert } from "antd";
import SettingsModal from "../../Modal/SettingsModal";
import FilterModal from "../WidgetsSettings/Filter/FilterSettingsModal";
import Widget from "../../Widget/Widget";
import IntlMessages from "@util/IntlMessages";
import "./FilterWidget.less";
import { useDispatch, useSelector } from "react-redux";
import { updateSearchParams } from "../../../appRedux/actions/Setting";
import { useIntl } from "react-intl";
import { isActivePublicUser, isOwnerOrModerator } from "../../../util/Scripts";
import { addPreset, deletePreset, updatePreset, getForm } from "../../../services/restAPI/RestApi";
import { setWidgetInSheetParams } from "../../../appRedux/actions/Boards";
import { Message, Button, Switch, Spin, Input, Popconfirm } from "../../BP2Components";
import { Icon_Delete, Icon_Edit, Icon_Plus } from "../../BP2Components/Icons/icons";
import { getFormWithData } from "../../../services/restAPI/FormsV3";
import { getDataForSelectFilters } from "../../../services/restAPI/ListsV2";
import { looksLikeDateValue, normalizeDateValue } from "../../../util/looksLikeDateValue";
import { encode } from "js-base64";

let FilterWidget = ({ value, role, form }) => {
   const intl = useIntl();
   const dispatch = useDispatch();
   const { id: userId } = useSelector(({ user }) => user);
   const [chosenPresetId, setChosenPresetId] = useState(null);
   const [presetInfo, setPresetInfo] = useState({ name: "", isPrivate: false, ownerId: null });
   const [availablePresets, setAvailablePresets] = useState([]);
   const [formFields, setFormFields] = useState([]);
   const [dictOptions, setDictOptions] = useState({});
   const [emptyOptions, setEmptyOptions] = useState({ empty_option: "-" });
   const inflightRequestsRef = useRef(new Map());
   const { Option } = Select;

   const addFilterToUrl = (widgetId, columnTechname, rawValue) => {
      try {
         const url = new URL(window.location);
         const raw = url.searchParams.get("listFilters");
         const currentListUrlFilters = raw ? JSON.parse(decodeURIComponent(raw)) : null;

         const value = looksLikeDateValue(rawValue) ? normalizeDateValue(rawValue) : rawValue;

         const storedValue =
            typeof value === "string" && value.includes("/") ? "b64:" + encode(value) : value;

         const next = currentListUrlFilters ? { ...currentListUrlFilters } : {};
         if (!next[widgetId]) next[widgetId] = {};
         next[widgetId][columnTechname] = storedValue;

         url.searchParams.set("listFilters", encodeURIComponent(JSON.stringify(next)));
         window.history.replaceState({}, "", url);
         dispatch(updateSearchParams(url.search));
      } catch (err) {
         console.log("Error in adding filter to URL", err);
      }
   };

   const deleteFilterFromUrl = (widgetId, columnTechname) => {
      try {
         const url = new URL(window.location);
         const raw = url.searchParams.get("listFilters");
         const currentListUrlFilters = raw ? JSON.parse(decodeURIComponent(raw)) : null;

         if (currentListUrlFilters?.[widgetId]?.[columnTechname]) {
            // If there is only one filter for one widget, we can delete query entirely
            if (
               Object.keys(currentListUrlFilters)?.length === 1 &&
               Object.keys(currentListUrlFilters[widgetId])?.length === 1
            ) {
               url.searchParams.delete("listFilters");
               window.history.replaceState({}, "", url);
               dispatch(updateSearchParams(url.search));

               // If this is the last filter of given widget, we can clear query of info of this widget
            } else if (
               Object.keys(currentListUrlFilters)?.length > 1 &&
               Object.keys(currentListUrlFilters[widgetId])?.length === 1
            ) {
               delete currentListUrlFilters[widgetId];

               url.searchParams.set(
                  "listFilters",
                  encodeURIComponent(JSON.stringify(currentListUrlFilters)),
               );
               window.history.replaceState({}, "", url);
               dispatch(updateSearchParams(url.search));

               // In other situations, just remove the property from the object
            } else {
               delete currentListUrlFilters[widgetId][columnTechname];

               url.searchParams.set(
                  "listFilters",
                  encodeURIComponent(JSON.stringify(currentListUrlFilters)),
               );
               window.history.replaceState({}, "", url);
               dispatch(updateSearchParams(url.search));
            }
         }
      } catch (err) {
         console.log("Error in deleting filter from URL", err);
      }
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      const { connections } = value;
      try {
         if (connections?.length > 0) {
            connections.forEach((connection) => {
               const fieldConnections = connection.filteredColumnTechNames.split(";");
               fieldConnections?.forEach((fieldConnection) => {
                  const [widgetIdForUrl, columnTechnameForUrl] = fieldConnection.split("|");
                  const fieldValue = form.getFieldValue(connection.fieldTechName);
                  if (widgetIdForUrl && columnTechnameForUrl) {
                     const emptyOptionValues = Object.values(emptyOptions);
                     if (!fieldValue || emptyOptionValues.find((el) => el === fieldValue)) {
                        deleteFilterFromUrl(widgetIdForUrl, columnTechnameForUrl);
                        return;
                     }
                     addFilterToUrl(widgetIdForUrl, columnTechnameForUrl, fieldValue);
                  }
               });
            });
         }
      } catch (error) {
         Message.error(intl.formatMessage({ id: "filterWidget.errorProcessingConnections" }));
      }
   };

   const clearFormFields = () => {
      const formField = form.getFieldsValue();
      Object.keys(formField).forEach((key) => {
         form.setFieldsValue({
            [key]: "",
         });
      });

      const url = new URL(window.location);
      url.searchParams.delete("listFilters");
      window.history.replaceState({}, "", url);
      dispatch(updateSearchParams(url.search));
   };

   const handleSetPreset = (presetId) => {
      setChosenPresetId(presetId);
      const presetToApply = availablePresets.find((preset) => preset.id == presetId);
      form.resetFields();
      if (presetToApply) {
         form.setFieldsValue(presetToApply.fields);
      }
   };

   const handleDeletePreset = (presetId) => async () => {
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      try {
         const res = await deletePreset(value.id, presetId);
         setAvailablePresets(handlePresetsAvailability(res.data, value.availablePresets, userId));
         dispatch(setWidgetInSheetParams({ ...value, savedFilters: res.data }));
         setChosenPresetId(null);
         Message.destroy();
         Message.success(intl.formatMessage({ id: "global.done" }));
      } catch (err) {
         Message.destroy();
         Message.error(intl.formatMessage({ id: "global.error" }));
      }
   };

   const handleAddPreset = (presetDetails, ownerId, fields) => async () => {
      const { name, isPrivate } = presetDetails;
      if (
         typeof name === "string" &&
         name !== "" &&
         !availablePresets.find((item) => item.name === presetInfo.name)
      ) {
         Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
         try {
            const res = await addPreset(value.id, {
               name,
               isPrivate: isPrivate ?? false,
               ownerId,
               fields,
            });
            setAvailablePresets(
               handlePresetsAvailability(res.data, value.availablePresets, userId),
            );
            dispatch(setWidgetInSheetParams({ ...value, savedFilters: res.data }));
            resetPresetInputs();
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
            setChosenPresetId(res.data.find((item) => item.name === name)?.id);
         } catch (err) {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         }
      } else if (availablePresets.find((item) => item.name === presetInfo.name)) {
         Message.error(intl.formatMessage({ id: "filter.errorIncorrectPresetName" }));
      }
   };

   const handleEditPreset = (presetDetails, fields) => async () => {
      const { id, name, isPrivate, ownerId } = presetDetails;
      if (
         typeof name === "string" &&
         name !== "" &&
         typeof isPrivate === "boolean" &&
         !availablePresets.find(
            (item) => item.name === presetInfo.name && item.id !== presetInfo.id,
         )
      ) {
         Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
         try {
            const res = await updatePreset(value.id, {
               id,
               name,
               isPrivate: isPrivate ?? false,
               ownerId,
               fields,
            });
            setAvailablePresets(
               handlePresetsAvailability(res.data, value.availablePresets, userId),
            );
            dispatch(setWidgetInSheetParams({ ...value, savedFilters: res.data }));
            resetPresetInputs();
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
            setChosenPresetId(id);
         } catch (err) {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         }
      } else if (
         availablePresets.find((item) => item.name === presetInfo.name && item.id !== presetInfo.id)
      ) {
         Message.error(intl.formatMessage({ id: "filter.errorIncorrectPresetName" }));
      }
   };

   const handlePresetInfoUpdate = (value, key) => {
      setPresetInfo((prevState) => ({ ...prevState, [key]: value }));
   };

   /** Reset fields in add and edit modals of presets */
   const resetPresetInputs = () => {
      setPresetInfo({ name: "", isPrivate: false, fields: {} });
   };

   const handleInitialEditInputs = (presetId) => () => {
      const presetToEdit = availablePresets.find((preset) => preset.id === presetId);
      if (!presetToEdit) return;
      setPresetInfo({ ...presetToEdit });
   };

   const handlePresetsAvailability = useCallback((presets, availability, ownerId) => {
      if (!presets) return [];
      switch (availability) {
         case "private":
            return presets.filter((preset) => preset.isPrivate && preset.ownerId === ownerId);
         case "public":
            return presets.filter((preset) => !preset.isPrivate);
         default:
            return presets.filter((preset) => {
               if (preset.isPrivate) {
                  return preset.ownerId === ownerId;
               }
               return preset;
            });
      }
   }, []);

   const normalizeOptions = (arr) =>
      (arr ?? []).map(({ value, label }) => ({
         value: String(value ?? ""),
         label: String(label ?? value ?? ""),
      }));

   const ensureDictLoaded = useCallback(
      async (techName, dataUrl) => {
         if (!techName || !dataUrl) return;

         if (Array.isArray(dictOptions[techName])) return;

         const inflight = inflightRequestsRef.current.get(techName);
         if (inflight) {
            await inflight;
            return;
         }

         const request = (async () => {
            try {
               const { data } = await getDataForSelectFilters(dataUrl);
               setDictOptions((cache) => ({ ...cache, [techName]: normalizeOptions(data) }));
            } catch (e) {
               console.error("Options fetch failed:", techName, e);
               setDictOptions((cache) => ({ ...cache, [techName]: [] }));
            } finally {
               inflightRequestsRef.current.delete(techName);
            }
         })();
         inflightRequestsRef.current.set(techName, request);
         await request;
      },
      [dictOptions],
   );

   const getDictOptions = (field) => {
      if (field?.dataUrl) {
         const techName = field.tech_name;
         const cachedOptions = dictOptions[techName];
         return (cachedOptions ?? []).map(({ value, label }) => (
            <Option id={value} key={value} value={value}>
               {label}
            </Option>
         ));
      } else {
         let sortedOptions = Array.isArray(field.sortedOptions) ? field.sortedOptions : [];
         const obj = field.object || {};
         if (obj.UIP_NUM4 === "1") {
            if (obj.UIP_TEKST4 || obj.UIP_TEKST3) {
               const newEmptyOptions = { ...emptyOptions };
               newEmptyOptions[obj.UIP_TEKST4] = obj.UIP_TEKST3;
               if (JSON.stringify(emptyOptions) !== JSON.stringify(newEmptyOptions)) {
                  setEmptyOptions(newEmptyOptions);
               }
            }
            sortedOptions = [
               { [obj.UIP_TEKST4 || "empty_option"]: obj.UIP_TEKST3 || "-" },
               ...sortedOptions,
            ];
         }
         return sortedOptions.map((item) => {
            const key = Object.keys(item)[0];
            return (
               <Option id={key} key={key} value={item[key]}>
                  {item[key]}
               </Option>
            );
         });
      }
   };

   useEffect(() => {
      if (!window) {
         return;
      }
      const searchParams = new URL(window.location)?.search;

      dispatch(updateSearchParams(searchParams));
   }, []);

   useEffect(() => {
      const isV3 = value.formId?.toString().length > 10;
      if (value.formId) {
         if (isV3) {
            getFormWithData(value.formId, null).then((res) => {
               const elements = res?.data?.fields;
               const prepareDataSet = elements.map((element) => {
                  const hideDec = (element.decorators ?? []).find(
                     (decorator) => decorator.name === "HideFieldDecorator",
                  );
                  const hiddenFlag = hideDec?.properties?.hide;
                  return {
                     ...element,
                     hidden: hiddenFlag,
                     tech_name: element.id,
                  };
               });
               setFormFields(prepareDataSet);
            });
         } else {
            getForm(value.formId, null, null, null, null, true).then((res) => {
               const tabsElements = res?.data?.default?.mainForm?.forms_data?.tab.map((singleTab) =>
                  Object.values(singleTab.elements),
               );
               const allFields = [...new Set(...tabsElements)];
               setFormFields(allFields);
            });
         }

         if (value.connections?.length) {
            const urlParams = new URLSearchParams(window.location.search);

            const raw = urlParams.get("listFilters");
            const currentListUrlFilters = raw ? JSON.parse(decodeURIComponent(raw)) : null;

            if (currentListUrlFilters) {
               value.connections.forEach((connection) => {
                  const pairs = (connection?.filteredColumnTechNames || "")
                     .split(";")
                     .map((string) => string.split("|"))
                     .filter(([widgetId, columnName]) => widgetId && columnName);
                  for (const [widgetId, columnName] of pairs) {
                     const filterValue = currentListUrlFilters?.[widgetId]?.[columnName];
                     if (filterValue !== undefined) {
                        form.setFieldsValue({ [connection.fieldTechName]: filterValue });
                        break;
                     }
                  }
               });
            }
         }
      }
   }, [value.formId]);

   useEffect(() => {
      setAvailablePresets(
         handlePresetsAvailability(value.savedFilters, value.availablePresets, userId),
      );
      setChosenPresetId(null);
      clearFormFields();
   }, [value.availablePresets]);

   useEffect(() => {
      const fieldsWithUrl = (formFields ?? []).filter(
         (field) => !!field?.dataUrl && !!field?.tech_name,
      );

      const uniqueByTech = Array.from(
         new Map(fieldsWithUrl.map((field) => [field.tech_name, field])).values(),
      );

      uniqueByTech.forEach((field) => {
         void ensureDictLoaded(field.tech_name, field.dataUrl);
      });
   }, [formFields, ensureDictLoaded]);

   return (
      <Widget
         className={`${value.layout === "horizontal" && "horizontal-wrapper"} filter-widget`}
         bgColor={value.backgroundColourHex}
         title={value.noteTitle}
         role={role}
         extra={
            (role === "OWNER" || role === "MODERATOR") && (
               <SettingsModal
                  widgetName={value.name}
                  widgetId={value.id}
                  icon="setting"
                  deleteIcon="true"
                  children={<FilterModal value={value} />}
               />
            )
         }
      >
         <div className="filter-widget__content-container">
            {value?.formId && formFields?.length === 0 ? (
               <Spin className="spin-middle" />
            ) : (
               <>
                  <label className="presets-label">
                     <IntlMessages id="presets.filterProfile" />
                  </label>
                  <div className="presets">
                     <Select
                        showSearch
                        filterOption={(input, { props }) => {
                           return props.value && props.children
                              ? props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                              : null;
                        }}
                        onSelect={handleSetPreset}
                        value={chosenPresetId}
                        placeholder={intl.formatMessage({ id: "presets.placeholder" })}
                     >
                        <Option value={null} key="null">
                           -
                        </Option>
                        {availablePresets.map((preset, index) => (
                           <Option value={preset.id} key={preset.id ?? index}>
                              {preset.name}
                           </Option>
                        ))}
                     </Select>
                     {!isActivePublicUser() &&
                        //TODO:  Change name of this option to proper name
                        value.isSaveAsVisible && (
                           <div className="presets__buttons">
                              <Popconfirm
                                 title={
                                    <div className="presets__buttons__modal">
                                       {availablePresets.find(
                                          (item) => item.name === presetInfo.name,
                                       ) && (
                                          <Alert
                                             message={
                                                <IntlMessages id="filterWidget.warningDoublePresetName" />
                                             }
                                             type="error"
                                          />
                                       )}
                                       <IntlMessages id="presets.presetName" />
                                       <Input
                                          onChange={({ target }) =>
                                             handlePresetInfoUpdate(target.value, "name")
                                          }
                                          size="small"
                                          value={presetInfo.name}
                                       />
                                       <IntlMessages id="presets.isPrivate" />
                                       <Switch
                                          onChange={(checked) =>
                                             handlePresetInfoUpdate(checked, "isPrivate")
                                          }
                                          size="small"
                                          checked={presetInfo.isPrivate}
                                       />
                                    </div>
                                 }
                                 onCancel={resetPresetInputs}
                                 onConfirm={handleAddPreset(
                                    presetInfo,
                                    userId,
                                    form.getFieldsValue(),
                                 )}
                              >
                                 <Button
                                    htmlType="button"
                                    buttonType="primary"
                                    ghost
                                    onClick={resetPresetInputs}
                                 >
                                    <Icon_Plus />
                                 </Button>
                              </Popconfirm>

                              {chosenPresetId &&
                                 (isOwnerOrModerator(role) ||
                                    !!availablePresets.find(
                                       (preset) => preset.ownerId === userId,
                                    )) && (
                                    <>
                                       <Popconfirm
                                          title={
                                             <div className="presets__buttons__modal">
                                                {availablePresets.find(
                                                   (item) =>
                                                      item.name === presetInfo.name &&
                                                      item.id !== presetInfo.id,
                                                ) && (
                                                   <Alert
                                                      message={
                                                         <IntlMessages id="filterWidget.warningDoublePresetName" />
                                                      }
                                                      type="error"
                                                   />
                                                )}
                                                <IntlMessages id="presets.presetName" />
                                                <Input
                                                   onChange={({ target }) =>
                                                      handlePresetInfoUpdate(target.value, "name")
                                                   }
                                                   size="small"
                                                   value={presetInfo.name}
                                                />
                                                <IntlMessages id="presets.isPrivate" />
                                                <Switch
                                                   onChange={(checked) =>
                                                      handlePresetInfoUpdate(checked, "isPrivate")
                                                   }
                                                   size="small"
                                                   checked={presetInfo.isPrivate}
                                                />
                                             </div>
                                          }
                                          onCancel={resetPresetInputs}
                                          onConfirm={handleEditPreset(
                                             presetInfo,
                                             form.getFieldsValue(),
                                          )}
                                       >
                                          <Button
                                             htmlType="button"
                                             buttonType="primary"
                                             ghost
                                             onClick={handleInitialEditInputs(chosenPresetId)}
                                          >
                                             <Icon_Edit />
                                          </Button>
                                       </Popconfirm>
                                       <Popconfirm
                                          title={intl.formatMessage({
                                             id: "settings.sureToDelete",
                                          })}
                                          onConfirm={handleDeletePreset(chosenPresetId)}
                                       >
                                          <Button htmlType="button" buttonType="danger" ghost>
                                             <Icon_Delete />
                                          </Button>
                                       </Popconfirm>
                                    </>
                                 )}
                           </div>
                        )}
                  </div>
                  <Form
                     className={`${
                        value.layout === "horizontal" ? "horizontal-view" : "filter-fields"
                     } `}
                     labelCol={{
                        span: 8,
                        style: { paddingLeft: 0, width: "fit-content" },
                     }}
                     wrapperCol={{ style: { width: "100%" } }}
                     layout={"vertical"}
                     onSubmit={handleSubmit}
                  >
                     <div className="formItemWrapper">
                        {formFields
                           ?.filter((field) => field.hidden !== "1")
                           ?.map((field) => {
                              if (field.type == "select") {
                                 return (
                                    <Form.Item
                                       key={field.tech_name}
                                       className="filter-fields__field"
                                       label={field.label}
                                    >
                                       {form.getFieldDecorator(field.tech_name)(
                                          <Select
                                             showSearch
                                             filterOption={(input, { props }) => {
                                                return props.value && props.children
                                                   ? props.children
                                                        .toLowerCase()
                                                        .indexOf(input.toLowerCase()) >= 0
                                                   : null;
                                             }}
                                          >
                                             {getDictOptions(field)}
                                          </Select>,
                                       )}
                                    </Form.Item>
                                 );
                              } else if (field?.type == "sql") {
                                 const setWithValues = new Set();
                                 return (
                                    <Form.Item
                                       key={field.tech_name}
                                       className="filter-fields__field"
                                       label={field.label}
                                    >
                                       {form.getFieldDecorator(field.tech_name)(
                                          <Select
                                             showSearch
                                             filterOption={(input, { props }) => {
                                                return props.value && props.children
                                                   ? props.children
                                                        .toLowerCase()
                                                        .indexOf(input.toLowerCase()) >= 0
                                                   : null;
                                             }}
                                          >
                                             {field?.sqlValues?.map((item) => {
                                                const key = item.value;
                                                const value = item.label;

                                                if (
                                                   String(value).trim() === "" ||
                                                   setWithValues.has(value)
                                                )
                                                   return;

                                                setWithValues.add(value);

                                                return (
                                                   <Option id={key} key={key} value={value}>
                                                      {value}
                                                   </Option>
                                                );
                                             })}
                                          </Select>,
                                       )}
                                    </Form.Item>
                                 );
                              } else {
                                 return (
                                    <Form.Item
                                       key={field.tech_name}
                                       className="filter-fields__field"
                                       label={field.label}
                                    >
                                       {form.getFieldDecorator(field.tech_name)(
                                          <Input className="filter-fields__field__input" />,
                                       )}
                                    </Form.Item>
                                 );
                              }
                           })}
                     </div>
                     <div className="buttonWrapper">
                        {/* This elements needs to be hidden, so filter on enter works correctly. If there is no submit button in DOM, you can't submit form by clicking ENTER */}
                        <Form.Item
                           style={{
                              display: !value?.showFilterButton && "none",
                           }}
                        >
                           <Button htmlType="submit" buttonType="primary" className="submit-btn">
                              {intl.formatMessage({ id: "filter.search" })}
                           </Button>
                        </Form.Item>
                        <Form.Item>
                           <Button
                              onClick={clearFormFields}
                              buttonType="primary"
                              ghost
                              className="submit-btn"
                           >
                              {intl.formatMessage({ id: "filter.reset" })}
                           </Button>
                        </Form.Item>
                     </div>
                  </Form>
               </>
            )}
         </div>
      </Widget>
   );
};

FilterWidget = Form.create({ name: "filterWidget" })(FilterWidget);

export default FilterWidget;
